/* eslint-disable @typescript-eslint/no-explicit-any -- any используется как значение параметров инвариантных типов */

import { Dictionary, IsAny, IsNever, IsUnion, Prettify, UnionToIntersection } from '@direct-frontend/stdlib';

import { BaseValidationError } from '../types';

import { ArrayField, ArrayFieldInstance, BaseArrayField, BaseArrayFieldInstance } from './createArrayField';
import { BaseObjectField, BaseObjectFieldInstance, ObjectField, ObjectFieldInstance } from './createObjectField';
import {
    BasePrimitiveField,
    BasePrimitiveFieldInstance,
    PrimitiveField,
    PrimitiveFieldInstance,
} from './createPrimitiveField';
import { BaseUnionField, BaseUnionFieldInstance, UnionField, UnionFieldInstance } from './createUnionField';

/** Ключ, за которым скрыты внутренние методы инстанса поля */
export const $internals = Symbol('$internals');
/** Ключ для сеттеров полей, который позволяет изменять значение поля в момент отправки формы */
export const $canSetWhileSubmitting = Symbol('$canSetWhileSubmitting');

export type SetValueOptions = {
    [$canSetWhileSubmitting]: boolean;
};

/** Базовый тип для любого поля */
export type BaseField = BasePrimitiveField | BaseObjectField | BaseArrayField | BaseUnionField;

/** Базовый тип для инстанса любого поля */
export type BaseFieldInstance =
    | BasePrimitiveFieldInstance
    | BaseObjectFieldInstance
    | BaseArrayFieldInstance
    | BaseUnionFieldInstance;

/** Базовый тип для объекта полей */
export type BaseFields = Dictionary<BaseField>;
/** Проверяет, что объект полей формы не является юнионом и не содержит опциональных полей */
export type ValidateFields<Fields extends BaseFields> =
    IsUnion<Fields> extends true
        ? // eslint-disable-next-line @adv-frontend/config/no-rus-string-locales-rule --- текст ошибки
          'Не используйте union-типы в качестве полей формы'
        : undefined extends Fields[keyof Fields]
          ? // eslint-disable-next-line @adv-frontend/config/no-rus-string-locales-rule --- текст ошибки
            'Поля формы не могут быть опциональными'
          : Fields;

/** Выводит тип значения поля */
export type InferFieldValue<Field extends BaseField> =
    // если поле "примитивное", то выводим тип его значения
    Field extends PrimitiveField<infer Value, any, never, any, any, any, any>
        ? Value // и возвращаем его
        : Field extends ObjectField<
                // если поле "объектное", то выводим его поля
                infer InnerFields extends BaseFields,
                any,
                never,
                any,
                any,
                any,
                any
            >
          ? { [FieldName in keyof InnerFields]: InferFieldValue<InnerFields[FieldName]> } // и возвращаем их значения
          : Field extends ArrayField<
                  // если поле – массив, то выводим тип его поля
                  infer InnerField extends BaseField,
                  any,
                  never,
                  any,
                  any,
                  any,
                  any
              >
            ? InferFieldValue<InnerField>[] // и возвращаем массив его значений
            : Field extends UnionField<
                    infer _,
                    // если поле – юнион, то выводим тип его элементов
                    infer UnionConstituents,
                    infer __
                >
              ? InferFieldValue<UnionConstituents[number]> // и возвращаем юнион их значений
              : never;

/** Выводит тип значений всех полей */
export type InferFieldsValue<Fields extends BaseFields> = Prettify<{
    [Field in keyof Fields]: InferFieldValue<Fields[Field]>;
}>;

/** Возвращает тип инстанса переданного поля */
export type FieldInstance<Field extends BaseField, FieldName extends string = string> = IsAny<
    Field,
    unknown,
    Field extends PrimitiveField<
        infer FieldValue,
        infer FieldValidationError,
        infer _,
        infer FieldContext,
        infer FieldActions,
        infer FieldSelectors,
        infer FieldAsyncActions
    >
        ? PrimitiveFieldInstance<
              FieldValue,
              FieldValidationError,
              FieldContext,
              FieldActions,
              FieldSelectors,
              FieldAsyncActions,
              FieldName
          >
        : Field extends ObjectField<
                infer InnerFields,
                infer FieldValidationError,
                infer _,
                infer FieldContext,
                infer FieldActions,
                infer FieldSelectors,
                infer FieldAsyncActions
            >
          ? ObjectFieldInstance<
                InnerFields,
                FieldValidationError,
                FieldContext,
                FieldActions,
                FieldSelectors,
                FieldAsyncActions,
                FieldName
            >
          : Field extends ArrayField<
                  infer InnerField,
                  infer FieldValidationError,
                  infer _,
                  infer FieldContext,
                  infer FieldActions,
                  infer FieldSelectors,
                  infer FieldAsyncActions
              >
            ? ArrayFieldInstance<
                  InnerField,
                  FieldValidationError,
                  FieldContext,
                  FieldActions,
                  FieldSelectors,
                  FieldAsyncActions,
                  FieldName
              >
            : Field extends UnionField<infer Key, infer UnionConstituents, infer UnionFieldActions>
              ? UnionFieldInstance<Key, UnionConstituents[number], UnionFieldActions, FieldName>
              : never
>;

/** Объект с контекстом для синхронного экшена поля */
export type FieldActionParams<FieldValue, FieldState, FieldContext> = {
    draftValue: FieldValue;
    getContext(): FieldContext;
    getState(): FieldState extends infer State ? State : never;
};

/** Тип экшена поля. Принимает контекст с draftValue, getContext, getState и произвольный набор аргументов */
export type FieldAction<FieldValue, FieldState, FieldContext, Args extends unknown[] = []> = (
    context: FieldActionParams<FieldValue, FieldState, FieldContext>,
    ...args: Args
) => void | undefined | FieldValue;
/** Объект со всеми экшенами в декларации поля */
export type FieldActionsMap<
    FieldValue,
    FieldState = never,
    FieldContext = never,
    Args extends unknown[] = any[],
> = Dictionary<FieldAction<FieldValue, FieldState, FieldContext, Args>>;
/** Базовый тип для любого объекта экшенов поля */
export type BaseFieldActionsMap = FieldActionsMap<any, any, any, any[]>;
/** Объект со всеми экшенами в инстансе поля */
export type BoundFieldActions<FieldActions> =
    FieldActionsMap<any, any, any, any> extends FieldActions
        ? never
        : {
              // Отбрасываем первый аргумент каждого экшена (context), так как контекст передается автоматически
              [FieldAction in keyof FieldActions]: FieldActions[FieldAction] extends (
                  ...args: [any, ...infer Args]
              ) => infer ReturnValue
                  ? (...args: Args) => ReturnValue
                  : never;
          };

/** Тип асинхронного экшена поля. Принимает инстанс поля, signal для отмены и произвольный набор аргументов */
export type FieldAsyncAction<FieldInstance, Args extends unknown[] = []> = (
    fieldInstance: FieldInstance,
    signal: AbortSignal,
    ...args: Args
) => Promise<void>;
/** Объект со всеми асинхронными экшенами в декларации поля */
export type FieldAsyncActionsMap<FieldInstance, Args extends unknown[] = any[]> = Dictionary<
    FieldAsyncAction<FieldInstance, Args>
>;
/** Базовый тип для любого объекта асинхронных экшенов поля */
export type BaseFieldAsyncActionsMap = FieldAsyncActionsMap<any, any[]>;
/** Объект со всеми асинхронными экшенами в инстансе поля */
export type BoundFieldAsyncActions<FieldAsyncActions> =
    FieldAsyncActionsMap<any, any> extends FieldAsyncActions
        ? never
        : {
              // Отбрасываем первый и второй аргументы каждого экшена (fieldInstance и signal), так как они передаются автоматически
              [FieldAsyncAction in keyof FieldAsyncActions]: FieldAsyncActions[FieldAsyncAction] extends (
                  ...args: [any, any, ...infer Args]
              ) => infer ReturnValue
                  ? (...args: Args) => ReturnValue
                  : never;
          };

/** Отбрасывает экшены, если они не переданы и TypeScript не вывел их тип */
export type SkipUnknownActions<FieldValue, FieldState, FieldContext, FieldActions> =
    FieldActionsMap<FieldValue, FieldState, FieldContext, any[]> extends FieldActions ? never : FieldActions;

/** Отбрасывает асинхронные экшены, если они не переданы и TypeScript не вывел их тип */
export type SkipUnknownAsyncActions<FieldAsyncActions> =
    FieldAsyncActionsMap<any, any> extends FieldAsyncActions ? never : FieldAsyncActions;

/** Тип селектора поля. Принимает текущее значение поля и произвольный набор аргументов */
export type FieldSelector<FieldValue, Args extends unknown[] = []> = (value: FieldValue, ...args: Args) => unknown;

/** Объект со всеми селекторами в декларации поля */
export type FieldSelectorsMap<FieldValue, Args extends unknown[] = any[]> = Dictionary<FieldSelector<FieldValue, Args>>;
/** Базовый тип для любого объекта селекторов поля */
export type BaseFieldSelectorsMap = FieldSelectorsMap<any, any[]>;
/** Объект со всеми селекторами в инстансе поля */
export type BoundFieldSelectors<FieldSelectors> = IsNever<
    FieldSelectors,
    never,
    FieldSelectorsMap<any, any> extends FieldSelectors
        ? never
        : {
              // Хук и геттер
              [FieldSelector in keyof FieldSelectors & string as
                  | `use${Capitalize<FieldSelector>}`
                  | FieldSelector]: FieldSelectors[FieldSelector] extends (
                  // Отбрасываем первый аргумент, так как текущее значение поля передается автоматически
                  ...args: [any, ...infer Args]
              ) => infer ReturnValue
                  ? (...args: Args) => ReturnValue
                  : never;
          }
>;

/** Отбрасывает селекторы, если они не переданы и TypeScript не вывел их тип */
export type SkipUnknownSelectors<FieldValue, FieldSelectors> =
    FieldSelectorsMap<FieldValue, any[]> extends FieldSelectors ? never : FieldSelectors;

/** Проверяет смогли ли мы вывести тип ошибок поля и если нет, то приводит его к `never` */
export type SkipUnknownValidationError<FieldValidationError extends BaseValidationError> =
    BaseValidationError extends FieldValidationError
        ? never
        : IsAny<
              // Из-за `any` в базовом типе, тип ошибок иногда может вывестись в any. Заменяем его на `never`
              FieldValidationError,
              never,
              FieldValidationError
          >;

// Выводит тип значения контекста переданного поля
export type InferFieldContext<Field extends BaseField> =
    Field extends PrimitiveField<
        infer _,
        infer __,
        infer ___,
        infer FieldContext,
        infer ____,
        infer _____,
        infer ______
    >
        ? FieldContext
        : Field extends ObjectField<
                infer _,
                infer __,
                infer ___,
                infer FieldContext,
                infer ____,
                infer _____,
                infer ______
            >
          ? FieldContext
          : Field extends ArrayField<
                  infer _,
                  infer __,
                  infer ___,
                  infer FieldContext,
                  infer ____,
                  infer _____,
                  infer ______
              >
            ? FieldContext
            : Field extends UnionField<infer _, infer UnionConstituents, infer _____>
              ? InferFieldContext<UnionConstituents[number]>
              : never;

// Выводит тип данных для заполнения контекста переданного поля
export type InferFieldContextInput<Field extends BaseField> =
    Field extends PrimitiveField<
        infer _,
        infer __,
        infer FieldContextInput,
        infer ___,
        infer ____,
        infer _____,
        infer ______
    >
        ? FieldContextInput
        : Field extends ObjectField<
                infer _,
                infer __,
                infer FieldContextInput,
                infer ___,
                infer ____,
                infer _____,
                infer ______
            >
          ? FieldContextInput
          : Field extends ArrayField<
                  infer _,
                  infer __,
                  infer FieldContextInput,
                  infer ___,
                  infer ____,
                  infer _____,
                  infer ______
              >
            ? FieldContextInput
            : Field extends UnionField<infer _, infer UnionConstituents, infer __>
              ? // union превращаем в intersection, так как это значение будет передано в качества аргумента функции
                UnionToIntersection<InferFieldContextInput<UnionConstituents[number]>>
              : never;

// Отбрасывает все поля объекта, значение которых равно unknown или never
type OmitUnknownAndNever<T> = {
    [K in keyof T as unknown extends T[K] ? never : IsNever<T[K], never, K>]: T[K];
};

// Выводит тип данных для заполнения контекста по переданному объекту полей
export type InferFieldsContextInput<FormFields extends BaseFields> = OmitUnknownAndNever<{
    [K in keyof FormFields]: InferFieldContextInput<FormFields[K]>;
}>;

// Возвращает true, если переданное поле требует данные для заполнения контекста
type HasFieldContextInput<Field extends BaseField, Result = InferFieldContextInput<Field>> = IsAny<
    // Значение данных для заполнения контекста может быть выведено как any – считаем, что его нет
    Result,
    false,
    // Значение данных для заполнения контекста может быть выведено как never – считаем, что его нет
    IsNever<Result, false, unknown extends Result ? false : true>
>;

// Возвращает true, если хотя бы одно поле из переданного объекта требует данные для заполнения контекста
type HasFieldsContextInput<FormFields extends BaseFields> = {
    [K in keyof FormFields]: HasFieldContextInput<FormFields[K]>;
}[keyof FormFields];

// Возвращает IfTrue, если хотя бы одно поле из переданного объекта требует данные
// для заполнения контекста и IfFalse иначе
export type IfFieldsRequireContextInput<
    FormFields extends BaseFields,
    IfTrue,
    IfFalse,
    Result = HasFieldsContextInput<FormFields>,
    // Если объект FormFields пуст, то Result будет равен `never` – обрабатываем этот кейс отдельно
> = IsNever<Result, IfFalse, true extends Result ? IfTrue : IfFalse>;

// Превращает unknown и any в never
export type SkipUnknownOrAny<T> = unknown extends T ? never : IsAny<T, never, T>;
